import { observable, computed, action } from 'mobx'
import { RootStore, GENERAL_STORE, EDITOR_MONITOR_STORE } from 'store'
import { computedFn } from 'mobx-utils'
import { Api, preloadFrames } from 'utilities'
import { InteractiveArea, VideoEntityEvent, InteractiveAreaAPI, EntityClassTypes, IaAction, VideoEventTypes, Project } from 'models'
import { EDITOR_CONTEXT_MENU_STORE } from 'store/EditorContextMenuStore'
import { EDITOR_ACTION_HISTORY_STORE } from 'store/EditorActionHistoryStore'
import { generateContentFromGemini } from 'utilities/Gemini'

export const EDITOR_INTERACTIVE_AREAS_STORE = 'EDITOR_INTERACTIVE_AREAS_STORE'

export class EditorInteractiveAreasStore {
  rootStore: RootStore

  constructor (rootStore: RootStore) {
    this.rootStore = rootStore
  }

  @observable interactiveAreas: { [key: string]: InteractiveArea } = {}
  @observable events: { [key: string]: VideoEntityEvent } = {}
  @observable selectedInteractiveAreaIds: number[] = []

  @action.bound deleteEvent (eventDetails: VideoEntityEvent) {
    const {
      [EDITOR_CONTEXT_MENU_STORE]: { parseToApiIa }
    } = this.rootStore
    const iaToUpdate =
      this.getInteractiveAreaById(eventDetails.interactiveAreaId)
    iaToUpdate.events = iaToUpdate.events
      .filter(eventId => eventId !== eventDetails.eventId)

    delete this.events[eventDetails.eventId]

    this.saveIA(parseToApiIa(iaToUpdate))
  }

  @action.bound setSelectedInteractiveAreaIds (arr = []) {
    this.selectedInteractiveAreaIds = arr
  }

  @action.bound setInitialInteractiveAreas (interactiveAreas: InteractiveAreaAPI[]) {
    const events = {}
    const areas = {}

    interactiveAreas.forEach(ia => {
      ia.events.forEach(event => {
        events[event.eventId] = event
      })

      areas[ia.id] = {
        ...ia,
        shots: ia.shots.map(shot => shot.id),
        events: ia.events.map(event => event.eventId)
      }
    })

    this.interactiveAreas = areas
    this.events = events
  }

  @action.bound setEvent (event: VideoEntityEvent) {
    this.events[event.eventId] = event
  }

  @action.bound saveIALocally (ia: InteractiveAreaAPI) {
    ia.events.forEach(this.setEvent)

    this.interactiveAreas[ia.id] = {
      ...ia,
      shots: ia.shots.map(shot => shot.id),
      events: ia.events.map(event => event.eventId)
    }
  }

  @action.bound deleteIaById (id) {
    delete this.interactiveAreas[id]
  }

  @computed get selectedIAsNumber () {
    return this.selectedInteractiveAreaIds.length
  }

  @computed get selectedInteractiveAreas (): InteractiveArea[] {
    return this.selectedInteractiveAreaIds
      .map(this.rootStore[EDITOR_INTERACTIVE_AREAS_STORE].getInteractiveAreaById)
  }

  @computed get currentFrameInteractiveAreaIds () {
    return this.rootStore[EDITOR_MONITOR_STORE]
      .rectanglesForCurrentFrame
      .map(r => r.interactiveAreaId)
  }

  @computed get sortedEventsList (): VideoEntityEvent[] {
    return Object.values(this.events)
      .sort((a: VideoEntityEvent, b: VideoEntityEvent) => a.t - b.t)
  }

  @observable isIaSelected = computedFn(iaId => {
    return this.selectedInteractiveAreaIds.includes(iaId)
  })

  @observable getInteractiveAreaEvents = computedFn(iaId => {
    return this.getInteractiveAreaById(iaId).events
      .map(this.getEventById)
  })

  @observable getInteractiveAreasForEntity = computedFn((
    entityId: number,
    entityClass: EntityClassTypes
  ) => {
    return Object.values(this.interactiveAreas)
      .filter(ia => ia.entityId === entityId && ia.entityClass === entityClass)
  })

  getInteractiveAreaById = id => {
    return { ...this.interactiveAreas[id] }
  }

  getEventById = id => {
    return { ...this.events[id] }
  }

  saveIA = (area: InteractiveAreaAPI, iaAction?: IaAction) => {
    const {
      [GENERAL_STORE]: { handleApiError },
      [EDITOR_ACTION_HISTORY_STORE]: { setNoActionInProgress }
    } = this.rootStore
    return Api
      .post('/video/interactiveArea/save', area)
      .then((ia: InteractiveAreaAPI) => {
        this.saveIALocally(ia)
        if (iaAction) {
          iaAction.setAfterVersion(ia)
          setNoActionInProgress()
        }
        return ia
      })
      .catch(handleApiError)
  }

  saveIas = (payload: { ia: InteractiveAreaAPI, action: IaAction }[]) => {
    payload.forEach(({ ia, action }) => this.saveIA(ia, action))
  }

  preloadProducts = (project:Project) => {
    const { brands } = project
    const generateProducts = {}
    if (brands?.length) {
      for (const brand of brands) {
        const { products } = brand
        if (products?.length) {
          for (const product of products) {
            const { tags } = product
            if (tags) {
              const productTags = tags.split(',')
              for (const productTag of productTags) {
                if (!generateProducts[productTag]) {
                  generateProducts[productTag] = {}
                }
                if (!generateProducts[productTag][brand?.name]) {
                  generateProducts[productTag][brand?.name] = []
                }
                generateProducts[productTag][brand?.name].push(product)
              }
            }
          }
        }
      }
      return generateProducts
    }
    return {}
  }

  generateAutoInteractiveAreas = async (project) => {
    const {
      [EDITOR_MONITOR_STORE]: { descaleRectDetails }
    } = this.rootStore

    const iasList = []
    const { shotGroups, videoEntityId, urlSpriteL } = project
    const frames = await preloadFrames(urlSpriteL)
    // Exclude the not-relavent shots
    delete shotGroups['<1sec']
    delete shotGroups['none grouped shots']

    const originalWidth = 1000 // Gemini gives default width
    const originalHeight = 1000 // Gemini gives default height
    const newWidth = 1068 // canvas width
    const newHeight = 601 // canvas height
    const scaleX = newWidth / originalWidth
    const scaleY = newHeight / originalHeight

    for (const group of Object.values(shotGroups)) {
      const data = await generateContentFromGemini({ base64Image: frames[group[0].startFrame + 1].toDataURL('image/png') }) // Generate for Shot 1 only (assume same for all shots in group).
      for (const grp of group as Array<any>) {
        if (Array.isArray(data)) {
          for (const obj of data) {
            const r = descaleRectDetails({
              x: (obj.object_location[0] * scaleX),
              y: (obj.object_location[1] * scaleY),
              width: (obj.object_location[2] * scaleX) - (obj.object_location[0] * scaleX),
              height: (obj.object_location[3] * scaleY) - (obj.object_location[1] * scaleY)
            })

            const payload = {
              action: null,
              ia: {
                id: null,
                videoEntityId,
                interactiveAreaId: null,
                name: obj.object_name,
                entityId: null,
                entityClass: null,
                shots: [
                  {
                    id: grp.id
                  }
                ],
                events: [
                  {
                    id: null,
                    eventId: null,
                    interactiveAreaId: null,
                    t: grp.startFrame,
                    r,
                    type: VideoEventTypes.IN
                  },
                  {
                    id: null,
                    eventId: null,
                    interactiveAreaId: null,
                    t: grp.endFrame,
                    r,
                    type: VideoEventTypes.OUT
                  }]
              }
            }

            iasList.push(payload)
          }
        }
      }
    }

    if (iasList.length) {
      this.saveIas(iasList)
    }
  }
}
